import { ClassAttributes, HTMLAttributes, memo, useCallback, useContext, useMemo, useRef } from 'react';
import ReactMarkdown, { Components, ExtraProps } from 'react-markdown';
import { useTranslations } from 'next-intl';
import { useEvent } from 'effector-react';
import classNames from 'classnames';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkDirective from 'remark-directive';
import remarkDirectiveRehype from 'remark-directive-rehype';
import remarkGfm from 'remark-gfm';
import Tippy from '@tippyjs/react';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { openImageViewerEvent } from '@nx-frontend/shared/store/image-viewer';
import { getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  AiMentionType,
  ChatbotUserUploadedFile,
  PortalBorderType,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
  TranslatesFnType,
} from '@nx-frontend/shared/types';
import { getBoxFileUrl } from '@nx-frontend/shared/utils';
import { getFileTypeName } from '@nx-frontend/shared/utils/file-type';
import { trackCopyAIResponseToClipboard } from './analytics';
import { AnimatedWritingDots } from './animated-writing-dots';
import { ChatbotContext } from './chatbot-context';
import { ChatbotMessageActiveReasoning } from './chatbot-message-active-reasoning';
import { ChatbotMessageActiveToolCall } from './chatbot-message-active-tool-call';
import { ChatbotMessageContext } from './chatbot-message-context';
import { ChatbotMessageSources } from './chatbot-message-sources';
import { CitationContent } from './citation-content';
import { CitationIcon } from './citation-icon';
import { AITask } from './message-components/ai-task';
import { ChatbotMessage, ChatbotMessageRole, SystemMessageTypes } from './types';
import { fixMarkdownAttachments, getMentionIcon, insertCitations } from './util';

export interface ChatbotMessageProps {
  message: ChatbotMessage;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  isWriting: boolean;
  aiName: string;
  aiCustomLogoUrl: string | null;
  isLastMessage: boolean;
  onAddMessageToPage?: (text: string) => void;
  onFeedback?: (message: ChatbotMessage) => void;
  onStartEditMessage?: (message: ChatbotMessage) => void;
  disliked?: boolean;
  allowCreatePage?: boolean;
  displayMessageContext?: boolean;
  isAgent?: boolean;
}

interface AITaskPropsRaw {
  url: string;
  done: string;
  title: string;
  id: string;
  description: string;
  assigneeUserIds: string;
  deadlineDate: string;
  createdAt: string;
  labels: string;
  boardTitle?: string;
  taskListTitle: string;
}

const getLimitMessage = (isFreePlan: boolean, isOwner: boolean, t: TranslatesFnType) => {
  if (!isOwner) {
    return t('features.ai-helper.you_have_reached_the_limit');
  }
  if (isFreePlan) {
    return t('features.ai-helper.markdown.nearing_limits_upgrade');
  }
  return t('features.ai-helper.markdown.nearing_limits_add_on');
};

// Add this new component for file attachments
const MessageAttachedFiles = ({
  files,
  themeColors,
  themeStyle,
}: {
  files: ChatbotUserUploadedFile[];
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
}) => {
  const [openImageViewer] = useEvent([openImageViewerEvent]);
  const maxFilesToShow = 3;

  const openImage = (src: string, title: string) => {
    openImageViewer({ title, src });
  };

  return (
    <div className="flex flex-col gap-1 mt-2">
      {files.slice(0, maxFilesToShow).map((file, index) => {
        const isImage = file.type?.startsWith('image/');
        const fileName = file.filename;

        if (isImage) {
          const url = file.sfUUID ? getBoxFileUrl(file.sfUUID, { width: 288, height: 288 }) : file.tempFileUrl;
          if (url) {
            return (
              <div
                key={index}
                className="text-xs text-neutral-600 flex items-center gap-1 w-full cursor-pointer"
                onClick={() => openImage(url, fileName)}
              >
                <img
                  alt={fileName}
                  src={url}
                  className=" min-w-10 min-h-10 max-w-72 max-h-72 rounded"
                  title={fileName}
                />
              </div>
            );
          }
        }

        const url = file.sfUUID ? getBoxFileUrl(file.sfUUID) : file.tempFileUrl;

        return (
          <div
            onClick={() => {
              if (url) {
                window.open(url, '_blank');
              }
            }}
            className={classNames(
              'flex flex-row w-full px-3 rounded p-2',
              getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE),
              { 'cursor-pointer': Boolean(url) }
            )}
            key={index}
          >
            <div className="text-2xl mr-3 flex items-center justify-center bg-cyan-100 rounded-lg w-8 h-8">
              <Icon className="w-6 h-6 text-teal-500" icon={IconsMap.FILE} />
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="text-xs font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                {fileName}
              </div>
              <div className="text-[11px] text-gray-600">{getFileTypeName(file.type ?? '')}</div>
            </div>
          </div>
        );
      })}
      {files.length > maxFilesToShow && (
        <div className="text-xs text-neutral-600">
          + {files.length - maxFilesToShow} more {files.length - maxFilesToShow === 1 ? 'file' : 'files'}
        </div>
      )}
    </div>
  );
};

export const ChatbotMessageComponent = memo(
  ({
    message: chatMessage,
    themeColors,
    themeStyle,
    isWriting,
    aiName,
    aiCustomLogoUrl,
    isLastMessage,
    onAddMessageToPage,
    onFeedback,
    onStartEditMessage,
    disliked,
    allowCreatePage = true,
    displayMessageContext = false,
    isAgent,
  }: ChatbotMessageProps) => {
    let { message } = chatMessage;
    const { role, citations, documents, system } = chatMessage;
    const { toast } = useToast();
    const aiContext = useContext(ChatbotContext);
    const { onPageNavigate } = aiContext;
    const t = useTranslations();
    const reactMarkdownContainerRef = useRef<HTMLDivElement>(null);

    if (system && chatMessage.message === SystemMessageTypes.LIMIT_REACHED) {
      if ('isFreePlan' in aiContext && 'isOrgOwner' in aiContext) {
        message = getLimitMessage(aiContext.isFreePlan as boolean, aiContext.isOrgOwner as boolean, t);
      }
    }

    // highlight the citations in the message
    const messageContent = useMemo(() => {
      if (isWriting) {
        if (message) {
          return fixMarkdownAttachments(message);
        } else {
          return false;
        }
      }
      return insertCitations(fixMarkdownAttachments(message), citations);
    }, [isWriting, message, citations]);

    const onCopyMessage = useCallback(async () => {
      let messageHtml = '';
      let messageHtmlPlainText = '';

      if (reactMarkdownContainerRef.current) {
        const currentWrapperHtml = reactMarkdownContainerRef.current.innerHTML;
        const parser = new DOMParser();
        const doc = parser.parseFromString(currentWrapperHtml, 'text/html');
        const reactMarkdownContent = doc.querySelector('.react-markdown');
        if (reactMarkdownContent) {
          messageHtml = reactMarkdownContent.innerHTML;
          const plainTextDoc = parser.parseFromString(messageHtml, 'text/html');
          messageHtmlPlainText = plainTextDoc.body.textContent || '';
        }
      }

      try {
        if (window.parent !== window) {
          // for copying in browser extension
          window.parent.postMessage(
            {
              source: 'ai-helper',
              type: 'embedMessageCopy',
              text: messageHtmlPlainText,
              textHtml: messageHtml,
            },
            '*'
          );
        } else {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([messageHtml], { type: 'text/html' }),
              'text/plain': new Blob([messageHtmlPlainText], { type: 'text/plain' }),
            }),
          ]);
        }

        toast({
          title: (
            <span className="inline-flex items-center gap-2">
              <Icon icon={IconsMap.CHECK} className="w-4" /> {t('features.ai-helper.ai_response_copied')}
            </span>
          ),
        });

        trackCopyAIResponseToClipboard();
      } catch (err) {
        toast({ title: t('features.ai-helper.failed_to_copy_ai_response') });
      }
    }, [toast, t]);

    const onAddToPage = useCallback(() => {
      onAddMessageToPage?.(message);
    }, [message, onAddMessageToPage]);

    const onFeedbackClick = useCallback(() => {
      onFeedback?.(chatMessage);
    }, [chatMessage, onFeedback]);

    const onStartEditMessageClick = useCallback(() => {
      onStartEditMessage?.(chatMessage);
    }, [chatMessage, onStartEditMessage]);

    const getUserAvatar = useMemo(() => {
      if (role === ChatbotMessageRole.AI) return null;
      if (aiContext.user && aiContext.user.avatar) {
        const avatar = getBoxFileUrl(aiContext.user.avatar, {
          width: 48,
          height: 48,
        });
        if (avatar && typeof avatar === 'string') {
          return <img src={avatar} width={24} height={24} className="rounded-full object-fill" alt="" />;
        }
      } else {
        let userLetter = '';
        if (aiContext.user?.lastname && aiContext.user?.firstname) {
          userLetter =
            aiContext.user?.firstname.slice(0, 1).toUpperCase() + aiContext.user?.lastname.slice(0, 1).toUpperCase();
        } else if (aiContext.user?.username) {
          userLetter = aiContext.user?.username.slice(0, 2).toUpperCase();
        }
        return (
          <div
            className={classNames(
              'rounded-full overflow-hidden w-6 h-6 flex items-center justify-center text-[10px] uppercase font-semibold',
              getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE),
              getTextClassNames(themeColors, PortalTextType.SURFACE)
            )}
          >
            <span>{userLetter}</span>
          </div>
        );
      }
    }, [role, aiContext.user, themeColors, themeStyle]);

    let aiIcon = <Icon icon={IconsMap.MAGIC_1} className="w-4" />;
    let aiIconClassNames =
      'rounded-full overflow-hidden w-6 h-6 flex items-center justify-center text-xs uppercase font-semibold text-white';
    if (aiCustomLogoUrl) {
      aiIcon = (
        <img
          src={aiCustomLogoUrl}
          className="w-6 h-6 rounded-full object-fill"
          alt="AI Logo"
          style={{ objectFit: 'cover' }}
        />
      );
    } else {
      aiIconClassNames += ' bg-teal-500';
    }

    let sourcesComponent: React.ReactElement | null = null;
    if (documents && documents.length) {
      sourcesComponent = <ChatbotMessageSources documents={documents} />;
    }

    // Check if message has files
    const messageFiles = useMemo(() => {
      // This assumes files might be stored in an array within the message object
      // Adjust the property path based on your actual data structure
      return chatMessage.files || [];
    }, [chatMessage]);

    const userMessageActionsButtons = useMemo(() => {
      return !messageFiles.length ? (
        <button
          className="w-6 h-6 flex items-center justify-center transition-colors duration-200 hover:bg-neutral-200 rounded"
          onClick={onStartEditMessageClick}
        >
          <Icon icon={IconsMap.PENCIL} className="w-5" />
        </button>
      ) : (
        ''
      );
    }, [messageFiles.length, onStartEditMessageClick]);

    const aiMessageActionsButtons = useMemo(
      () => (
        <>
          <button
            className="w-6 h-6 flex items-center justify-center transition-colors duration-200 hover:bg-neutral-200 rounded"
            onClick={onCopyMessage}
          >
            <Icon icon={IconsMap.COPY} className="w-5" />
          </button>
          {allowCreatePage && (
            <button
              className="w-6 h-6 flex items-center justify-center transition-colors duration-200 hover:bg-neutral-200 rounded"
              onClick={onAddToPage}
            >
              <Icon icon={IconsMap.NOTE_PLUS} className="w-5" />
            </button>
          )}
          {!isAgent && (
            <button
              className={classNames(
                'transition-colors duration-200 rounded relative left-[2px]',
                'w-6 h-6 flex items-center justify-center',
                {
                  'cursor-default text-cyan-900': disliked,
                  'hover:bg-neutral-200': !disliked,
                }
              )}
              onClick={!disliked ? onFeedbackClick : undefined}
            >
              <Icon icon={IconsMap.DISLIKE} className="w-5" />
            </button>
          )}
        </>
      ),
      [allowCreatePage, disliked, isAgent, onAddToPage, onCopyMessage, onFeedbackClick]
    );

    const messageActionsButtons = (
      <div
        className={classNames(
          'flex gap-1 items-center group-hover/ai-message:opacity-100 transition-opacity duration-200',
          {
            'opacity-0': !isLastMessage,
          },
          getTextClassNames(themeColors, PortalTextType.SURFACE_3)
        )}
      >
        {role === ChatbotMessageRole.AI && !chatMessage.system && !isWriting && aiMessageActionsButtons}
        {role === ChatbotMessageRole.USER && userMessageActionsButtons}
      </div>
    );

    let answeringProcess: React.ReactElement | null = null;
    if (chatMessage.activeReasoning) {
      answeringProcess = <ChatbotMessageActiveReasoning message={chatMessage} />;
    } else if (chatMessage.activeToolCall) {
      answeringProcess = <ChatbotMessageActiveToolCall message={chatMessage} />;
    }

    return (
      <div
        className={classNames('ai-helper__message group/ai-message flex flex-col my-4 text-sm gap-3', {
          'items-end': role === ChatbotMessageRole.USER,
        })}
      >
        <div className="flex items-center justify-between">
          <span className="flex gap-2 items-center">
            {role === ChatbotMessageRole.USER ? getUserAvatar : <div className={aiIconClassNames}>{aiIcon}</div>}
            <span
              className={classNames('font-semibold text-sm', getTextClassNames(themeColors, PortalTextType.SURFACE))}
            >
              {role === ChatbotMessageRole.USER ? 'You' : aiName}
            </span>
          </span>
        </div>
        <div
          className={classNames('w-full max-w-full flex', {
            'justify-end': role === ChatbotMessageRole.USER,
          })}
        >
          <div
            className={classNames('leading-relaxed min-w-0', {
              'w-full': role === ChatbotMessageRole.AI && messageContent,
            })}
          >
            {messageContent ? (
              <div>
                <div
                  ref={reactMarkdownContainerRef}
                  className={classNames(
                    'react-markdown-container ai-helper__message-content chatbot-message rounded max-w-full border border-[#F4F4F5] relative',
                    {
                      'bg-[#FAFAFA]': role === ChatbotMessageRole.USER,
                      'bg-[#F0FDFA] !border-transparent': role === ChatbotMessageRole.AI && isWriting,
                      'chatbot-ai-message': role === ChatbotMessageRole.AI && !isWriting,
                      'w-full': role === ChatbotMessageRole.AI,
                    }
                  )}
                >
                  <ReactMarkdown
                    className={classNames(
                      'react-markdown prose prose-slate prose-sm max-w-full px-4 py-3 text-neutral-800',
                      {
                        'w-full': role === ChatbotMessageRole.AI,
                      }
                    )}
                    remarkPlugins={[remarkDirective, remarkDirectiveRehype, remarkGfm]}
                    // makes html tags, for example <br> render and  cleans anything risky
                    rehypePlugins={[rehypeRaw, rehypeSanitize]}
                    components={
                      {
                        'pre': ({ node, ...props }) => {
                          return <pre className="text-[#83ba52] bg-[#1D2A34] text-sm" {...props} />;
                        },
                        'blockquote': ({ node, ...props }) => {
                          const citationId =
                            'data-citation-id' in props ? (props['data-citation-id'] as string | undefined) : undefined;
                          const citation =
                            citationId && citations ? citations.find((c) => c.id === citationId) : undefined;
                          if (citation) {
                            const doc =
                              documents &&
                              citation.documentIds.length &&
                              citation.documentIds[0] &&
                              documents.find((document) => document.id === citation.documentIds[0]);
                            if (doc) {
                              return (
                                <div className="relative inline-block align-text-top ml-0.5">
                                  <Tippy
                                    content={
                                      <CitationContent doc={doc} currentPortalId={aiContext.portalSettings?.id} />
                                    }
                                    placement="top-start"
                                    arrow={false}
                                    delay={[200, 200]}
                                    duration={200}
                                    interactive={true}
                                    animation="scale"
                                    appendTo={document.body}
                                  >
                                    <div>
                                      <CitationIcon docType={doc.type} />
                                    </div>
                                  </Tippy>
                                </div>
                              );
                            }
                          }
                          return <blockquote {...props} />;
                        },
                        'a': ({ node, children, ...props }) => {
                          if ('data-link-role' in props) {
                            if (props['data-link-role'] === 'note') {
                              const globalId = 'data-global-id' in props && (props['data-global-id'] as string);
                              const onNoteLinkClick = () => {
                                if (globalId) {
                                  onPageNavigate(AiMentionType.NOTE, globalId);
                                }
                              };
                              return (
                                <span className="font-bold text-cyan-900 cursor-pointer" onClick={onNoteLinkClick}>
                                  {'data-type' in props && props['data-type'] === 'mention' ? (
                                    <Icon icon={IconsMap.NOTE} className="w-4 inline-block align-text-bottom" />
                                  ) : null}
                                  {children}
                                </span>
                              );
                            }
                            if (props['data-link-role'] === 'add-on') {
                              return (
                                <span
                                  className="font-bold text-cyan-900 cursor-pointer"
                                  onClick={aiContext.onShowAddonPopup}
                                >
                                  {children}
                                </span>
                              );
                            }
                            if (props['data-link-role'] === 'upgrade') {
                              return (
                                <span
                                  className="font-bold text-cyan-900 cursor-pointer"
                                  onClick={aiContext.onShowUpgradePopup}
                                >
                                  {children}
                                </span>
                              );
                            }
                          } else {
                            if (props.href) {
                              // check that workspace is matched current workspace and it is page link
                              const { href } = props;
                              const m = href.match(/\/space\/([^/]+)\/page\/([^/]+)$/);
                              const cm = document.location.href.match(/\/space\/([^/]+)\/?/);
                              if (m && cm) {
                                if (m[1] === cm[1]) {
                                  const globalId = m[2];
                                  const onNoteLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
                                    event.preventDefault();
                                    if (globalId) {
                                      onPageNavigate(AiMentionType.NOTE, globalId);
                                    }
                                  };
                                  return (
                                    <a
                                      href={href}
                                      className="font-bold text-cyan-900 cursor-pointer no-underline"
                                      onClick={onNoteLinkClick}
                                    >
                                      {children}
                                    </a>
                                  );
                                }
                              }

                              const tasksMatch = href.match(/\/space\/([^/]+)\/tasks\/([^/]+)\/?$/);
                              if (cm && tasksMatch && tasksMatch[1] === cm[1]) {
                                // process link to the tasks dashboard
                                const onTasksLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
                                  event.preventDefault();
                                  onPageNavigate('tasks', tasksMatch[2]);
                                };
                                return (
                                  <a
                                    href={href}
                                    className="font-bold text-cyan-900 cursor-pointer no-underline"
                                    onClick={onTasksLinkClick}
                                  >
                                    {children}
                                  </a>
                                );
                              }
                            }
                          }
                          return (
                            <a {...props} target="_blank">
                              {children}
                            </a>
                          );
                        },
                        'ai-mention': ({
                          node,
                          children,
                          ...props
                        }: ClassAttributes<HTMLSpanElement> & HTMLAttributes<HTMLSpanElement> & ExtraProps) => {
                          try {
                            const type = 'data-type' in props ? (props['data-type'] as string) : 'unknown';
                            const globalId = 'data-global-id' in props ? (props['data-global-id'] as string) : type;
                            const mentionUrl =
                              'data-mention-url' in props ? (props['data-mention-url'] as string) : false;
                            const onMentionClick = () => {
                              if (globalId && !mentionUrl) {
                                onPageNavigate(type as AiMentionType | 'workspace' | 'portal', globalId);
                              } else if (mentionUrl) {
                                window.open(mentionUrl, '_blank');
                              }
                            };
                            if (type && globalId) {
                              return (
                                <span
                                  className={classNames(
                                    'font-bold text-cyan-900 cursor-pointer gap-1 inline-flex items-center align-bottom'
                                  )}
                                  onClick={onMentionClick}
                                >
                                  <div className="rounded-full overflow-hidden text-[9px] flex items-center justify-center w-4 h-4">
                                    {getMentionIcon({
                                      id: globalId,
                                      display: children as string,
                                      type,
                                    })}
                                  </div>
                                  {children}
                                </span>
                              );
                            } else {
                              return <span {...props}>{children}</span>;
                            }
                          } catch (err) {
                            console.log(err);
                            return <span {...props}>{children}</span>;
                          }
                        },
                        'ai-task': ({ ...props }: AITaskPropsRaw) => {
                          return (
                            <AITask
                              {...props}
                              onClick={() => {
                                if (aiContext.portalId && aiContext.isVisitor) {
                                  onPageNavigate('tasks', props.id, {
                                    url: props.url,
                                  });
                                } else {
                                  onPageNavigate('tasks', props.id);
                                }
                              }}
                            />
                          );
                        },
                        'table': ({ node, ...props }) => (
                          <div
                            style={{
                              overflowX: 'auto',
                              maxWidth: '100%',
                            }}
                          >
                            <table
                              {...props}
                              style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                              }}
                            />
                          </div>
                        ),
                      } as Components
                    }
                  >
                    {messageContent}
                  </ReactMarkdown>
                  {sourcesComponent}
                  {displayMessageContext && chatMessage.messageContext && (
                    <ChatbotMessageContext message={chatMessage} />
                  )}
                </div>
                {messageFiles && messageFiles.length > 0 && (
                  <MessageAttachedFiles files={messageFiles} themeColors={themeColors} themeStyle={themeStyle} />
                )}
              </div>
            ) : isWriting ? (
              <div
                className={classNames('chatbot-message prose prose-slate prose-sm rounded px-4 py-3  max-w-full', {
                  'bg-[#F0F9FF]': role === ChatbotMessageRole.USER,
                  'bg-[#F0FDFA]': role === ChatbotMessageRole.AI && isWriting,
                  'bg-[#FAFAFA]': role === ChatbotMessageRole.AI && !isWriting,
                })}
              >
                {answeringProcess || <AnimatedWritingDots color="teal" revert={true} />}
              </div>
            ) : (
              <div
                className={classNames(
                  'chatbot-message chatbot-error-message prose prose-slate prose-sm rounded px-3 py-3 max-w-full border border-[#FFE4E6]'
                )}
              >
                {t('features.ai-helper.something_went_wrong')}
              </div>
            )}
            <div
              className={classNames('w-full flex mt-3', {
                'justify-begin': role === ChatbotMessageRole.AI,
                'justify-end': role === ChatbotMessageRole.USER,
              })}
            >
              {messageActionsButtons}
            </div>
          </div>
        </div>
      </div>
    );
  }
);
